import { useEffect, useState } from 'react';
import client from 'src/gatsby-plugin-apollo/client';
import { COUNTIES } from 'src/data';

export interface County {
  fips: string;
  name: string;
  state: string;
  zipcode: string;
}


export const getCounties = async (zip: string): Promise<County[]> => {
  if (zip.length !== 5) return [];

  const { data } = await client.query<{ counties: County[] }>({
    query: COUNTIES,
    variables: { zipCode: zip },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return data?.counties ?? [];
};


export const getCounty = async (zip: string, fips?: string): Promise<County | undefined> => {
  const counties = await getCounties(zip);
  if (!counties.length) return undefined;
  return fips ? counties.find(c => c.fips === fips) : counties[0];
};


export const useZipcode = (initialZip = '', options?: { assumeCounty?: boolean }) => {
  const [zip, setZip] = useState(initialZip);
  const [place, setPlace] = useState<County | undefined>();
  const [counties, setCounties] = useState<County[]>([]);
  const [message, setMessage] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  const isValidZip = zip.length === 5;

  const handleLookup = async (zipCode: string) => {
    setLookingUp(true);
    setMessage('Loading...');

    try {
      const allCounties = await getCounties(zipCode);
      setCounties(allCounties);

      if (!allCounties.length) {
        setPlace(undefined);
        setMessage("Sorry, this zip code either doesn't exist or is outside our service area");
        return;
      }

      // Select a county if only one exists or assumeCounty is true
      const selected = allCounties.length === 1 || options?.assumeCounty
        ? allCounties[0]
        : undefined;

      setPlace(selected);
      setMessage(selected ? `${selected.name}, ${selected.state}` : '');
    } catch {
      setPlace(undefined);
      setCounties([]);
      setMessage("Error fetching counties");
    } finally {
      setLookingUp(false);
    }
  };

  useEffect(() => {
    if (!isValidZip) {
      setPlace(undefined);
      setCounties([]);
      setMessage('');
      return;
    }
    handleLookup(zip);
  }, [zip, isValidZip]);

  return {
    zip,
    setZip,
    place,
    setPlace,
    counties,
    message,
    lookingUp,
    showCounties: counties.length > 1,
    isValidLocation: isValidZip && Boolean(place?.fips),
  };
};
